import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import GameCard from '../components/Games/GameCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const GamesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { api } = useAuth();
  const [games, setGames] = useState({
    templateGames: [],
    customGames: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    subject: searchParams.get('subject') || '',
    difficulty: searchParams.get('difficulty') || '',
    gameType: searchParams.get('gameType') || '',
    featured: searchParams.get('featured') || '',
    search: searchParams.get('search') || ''
  });
  const [activeTab, setActiveTab] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 12,
    hasMore: false
  });

  const subjects = [
    'mathematics', 'english', 'physics', 'chemistry', 'biology',
    'geography', 'economics', 'government', 'literature', 'history'
  ];

  const difficulties = ['beginner', 'intermediate', 'advanced'];

  const gameTypes = [
    'quiz', 'matching', 'word_puzzle', 'math_challenge', 'memory',
    'drag_drop', 'fill_blanks', 'multiple_choice'
  ];

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

const fetchGames = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const params = {
      ...filters,
      page: pagination.currentPage,
      limit: pagination.limit
    };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await api.get('/games', { params });
      
      if (response.data.success) {
        const { templateGames, customGames, pagination: paginationData } = response.data.data;
        
        if (pagination.currentPage === 1) {
          setGames({ templateGames, customGames });
        } else {
          // Append to existing games for pagination
          setGames(prev => ({
            templateGames: prev.templateGames,
            customGames: [...prev.customGames, ...customGames]
          }));
        }
        
        setPagination(prev => ({
          ...prev,
          hasMore: paginationData.hasMore
        }));
      }
  } catch (err) {
    console.error('Error fetching games:', err);
    setError('Failed to load games. Please try again.');
  } finally {
    setLoading(false);
  }
}, [filters, pagination.currentPage, pagination.limit, api]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      subject: '',
      difficulty: '',
      gameType: '',
      featured: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const loadMore = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: prev.currentPage + 1
    }));
  };

  const getFilteredGames = () => {
    let allGames = [];
    
    if (activeTab === 'all' || activeTab === 'template') {
      allGames = [...games.templateGames];
    }
    
    if (activeTab === 'all' || activeTab === 'custom') {
      allGames = [...allGames, ...games.customGames];
    }
    
    return allGames;
  };

  const filteredGames = getFilteredGames();
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  if (loading && pagination.currentPage === 1) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900">Games</h1>
          <p className="text-sm text-slate-500 mt-0.5">Learn through interactive games and challenges</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search games..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-200 focus:border-slate-300"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>🔍</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {['all', 'template', 'custom'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activeTab === tab ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  {tab === 'all' ? 'All games' : tab === 'template' ? 'Quick games' : 'Custom games'}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={filters.subject} onChange={(e) => handleFilterChange('subject', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <option value="">All subjects</option>
                {subjects.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <select value={filters.difficulty} onChange={(e) => handleFilterChange('difficulty', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <option value="">All difficulties</option>
                {difficulties.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
              <select value={filters.gameType} onChange={(e) => handleFilterChange('gameType', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <option value="">All types</option>
                {gameTypes.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={filters.featured === 'true'} onChange={(e) => handleFilterChange('featured', e.target.checked ? 'true' : '')} className="rounded border-slate-300 text-slate-900 focus:ring-slate-500" />
                Featured only
              </label>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="text-sm font-medium text-slate-600 hover:text-slate-900">Clear filters</button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6 flex items-center justify-between">
            <p className="text-sm text-red-800">{error}</p>
            <button type="button" onClick={fetchGames} className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200">Try again</button>
          </div>
        )}

        {filteredGames.length === 0 && !loading ? (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-12 text-center">
            <div className="text-4xl mb-4" aria-hidden>🎮</div>
            <h3 className="text-lg font-medium text-slate-900 mb-1">No games found</h3>
            <p className="text-sm text-slate-500 mb-4">
              {hasActiveFilters ? 'Try adjusting your filters.' : 'No games available at the moment.'}
            </p>
            {hasActiveFilters && (
              <button type="button" onClick={clearFilters} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Clear filters</button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredGames.map((game, index) => {
                const gameType = game.type || (game.id ? 'template' : 'custom');
                return <GameCard key={game._id || game.id || index} game={game} type={gameType} />;
              })}
            </div>
            {pagination.hasMore && (
              <div className="mt-6 text-center">
                <button type="button" onClick={loadMore} disabled={loading} className="px-4 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GamesPage;